import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Avatar,
  Chip,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { User, Bell, Clock, Palette, Save, Settings, Tag } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAppSelector, useAppDispatch } from '@/store';
import { selectCurrentUser, setUser } from '@/store/slices/authSlice';
import { userService, notificationService } from '@/services/api';
import { getMe, mapBackendUserToFrontend } from '@/services/api/authService';
import { useFeaturePermission } from '@/shared/contexts/PermissionsContext';
import ConfigTab from '../components/ConfigTab';
import TaskCategoriesTab from '../components/TaskCategoriesTab';
import { isAdminOrManager } from '@/shared/utils/roles';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

const STORAGE_KEYS = {
  TIMER: 'app_settings_timer',
  DISPLAY: 'app_settings_display',
} as const;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * SettingsPage
 * User preferences and application settings
 */
const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  
  // Users can always edit their own profile/preferences; admin config tab is separate
  const canEditAdminConfig = useFeaturePermission('settings', 'settings_management', 'edit');
  const canManageCategories = isAdminOrManager(user?.role);
  const canEditOwnSettings = true;
  const categoriesTabIndex = 4;
  const configTabIndex = canManageCategories ? 5 : 4;
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Profile settings (synced from user)
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [timezone, setTimezone] = useState(user?.timezone || 'America/New_York');
  const [workHoursPerDay, setWorkHoursPerDay] = useState(user?.workHoursPerDay ?? 8);

  // Notification settings (loaded from API)
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [taskReminders, setTaskReminders] = useState(true);
  const [deadlineAlerts, setDeadlineAlerts] = useState(true);
  const [reviewNotifications, setReviewNotifications] = useState(true);

  // Display settings (from localStorage)
  const [darkMode, setDarkMode] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [showTimerInHeader, setShowTimerInHeader] = useState(true);

  // Timer settings (from localStorage)
  const [autoStartTimer, setAutoStartTimer] = useState(false);
  const [pomodoroMode, setPomodoroMode] = useState(false);
  const [pomodoroLength, setPomodoroLength] = useState(25);
  const [breakLength, setBreakLength] = useState(5);

  useEffect(() => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setDisplayName(user?.displayName || '');
    setEmail(user?.email || '');
    setTimezone(user?.timezone || 'America/New_York');
    setWorkHoursPerDay(user?.workHoursPerDay ?? 8);
  }, [user?.id, user?.firstName, user?.lastName, user?.displayName, user?.email, user?.timezone, user?.workHoursPerDay]);

  const { data: notifPrefs } = useQuery({
    queryKey: ['settings', 'notificationPreferences'],
    queryFn: () => notificationService.getPreferences(),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (notifPrefs) {
      setEmailNotifications(notifPrefs.email !== false);
      setTaskReminders(notifPrefs.taskReminders !== false);
      setDeadlineAlerts(notifPrefs.deadlineAlerts !== false);
      setReviewNotifications(notifPrefs.reviewNotifications !== false);
    }
  }, [notifPrefs]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.TIMER);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.showTimerInHeader !== undefined) setShowTimerInHeader(parsed.showTimerInHeader);
        if (parsed.autoStartTimer !== undefined) setAutoStartTimer(parsed.autoStartTimer);
        if (parsed.pomodoroMode !== undefined) setPomodoroMode(parsed.pomodoroMode);
        if (parsed.pomodoroLength !== undefined) setPomodoroLength(Number(parsed.pomodoroLength));
        if (parsed.breakLength !== undefined) setBreakLength(Number(parsed.breakLength));
      }
      const displayStored = localStorage.getItem(STORAGE_KEYS.DISPLAY);
      if (displayStored) {
        const parsed = JSON.parse(displayStored);
        if (parsed.compactMode !== undefined) setCompactMode(parsed.compactMode);
      }
    } catch {
      // ignore invalid stored data
    }
  }, []);

  const profileMutation = useMutation({
    mutationFn: () =>
      userService.updateProfile({ firstName, lastName, displayName, timezone, workHoursPerDay }),
    onSuccess: async (updated) => {
      if (updated && typeof updated === 'object' && 'id' in updated && 'displayName' in updated) {
        const mapped = mapBackendUserToFrontend(updated as Parameters<typeof mapBackendUserToFrontend>[0]);
        if (typeof (updated as { workHoursPerDay?: number }).workHoursPerDay === 'number') {
          (mapped as { workHoursPerDay: number }).workHoursPerDay = (updated as { workHoursPerDay: number }).workHoursPerDay;
        }
        if (typeof (updated as { timezone?: string }).timezone === 'string') {
          (mapped as { timezone: string }).timezone = (updated as { timezone: string }).timezone;
        }
        dispatch(setUser(mapped));
      } else {
        const me = await getMe();
        dispatch(setUser(me));
      }
      setSnackbar({ open: true, message: 'Profile saved.', severity: 'success' });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to save profile.', severity: 'error' });
    },
  });

  const notifMutation = useMutation({
    mutationFn: () =>
      notificationService.updatePreferences({
        email: emailNotifications,
        taskReminders,
        deadlineAlerts,
        reviewNotifications,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'notificationPreferences'] });
      setSnackbar({ open: true, message: 'Notification preferences saved.', severity: 'success' });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to save preferences.', severity: 'error' });
    },
  });

  const handleSaveProfile = () => {
    profileMutation.mutate();
  };

  const handleSaveNotifications = () => {
    notifMutation.mutate();
  };

  const handleSaveTimer = () => {
    try {
      localStorage.setItem(
        STORAGE_KEYS.TIMER,
        JSON.stringify({
          showTimerInHeader,
          autoStartTimer,
          pomodoroMode,
          pomodoroLength,
          breakLength,
        })
      );
      setSnackbar({ open: true, message: 'Timer settings saved.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save timer settings.', severity: 'error' });
    }
  };

  const handleSaveDisplay = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.DISPLAY, JSON.stringify({ compactMode }));
      setSnackbar({ open: true, message: 'Display settings saved.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save display settings.', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: isMobile ? 10 : 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
          Settings
        </Typography>
        {!isMobile && (
          <Typography variant="body2" color="text.secondary">
            Manage your account and application preferences
          </Typography>
        )}
      </Box>

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{ 
            borderBottom: '1px solid', 
            borderColor: 'divider', 
            px: { xs: 1, sm: 2 },
            '& .MuiTab-root': {
              minWidth: isMobile ? 'auto' : 120,
              px: isMobile ? 1.5 : 2,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
          }}
        >
          <Tab label={isMobile ? 'Profile' : 'Profile'} icon={<User size={isMobile ? 14 : 16} />} iconPosition="start" />
          <Tab label={isMobile ? 'Alerts' : 'Notifications'} icon={<Bell size={isMobile ? 14 : 16} />} iconPosition="start" />
          <Tab label={isMobile ? 'Timer' : 'Timer & Work'} icon={<Clock size={isMobile ? 14 : 16} />} iconPosition="start" />
          <Tab label="Display" icon={<Palette size={isMobile ? 14 : 16} />} iconPosition="start" />
          {canManageCategories && (
            <Tab label={isMobile ? 'Categories' : 'Task Categories'} icon={<Tag size={isMobile ? 14 : 16} />} iconPosition="start" />
          )}
          {canEditAdminConfig && (
            <Tab label="Config" icon={<Settings size={isMobile ? 14 : 16} />} iconPosition="start" />
          )}
        </Tabs>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Profile Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ maxWidth: isMobile ? '100%' : 600 }}>
              {/* Avatar */}
              <Box sx={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: { xs: 2, sm: 3 }, mb: { xs: 3, sm: 4 }, flexDirection: isMobile ? 'column' : 'row' }}>
                <Avatar
                  sx={{
                    width: isMobile ? 64 : 80,
                    height: isMobile ? 64 : 80,
                    bgcolor: 'primary.main',
                    fontSize: isMobile ? '1.5rem' : '2rem',
                  }}
                >
                  {user?.firstName?.charAt(0) || 'U'}
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600}>
                    {user?.displayName || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                  <Chip
                    label={user?.role?.replace('_', ' ')}
                    size="small"
                    sx={{ mt: 0.5, textTransform: 'capitalize' }}
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Profile Form */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                  />
                  <TextField
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Box>

                <TextField
                  label="Display Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  fullWidth
                  size={isMobile ? 'small' : 'medium'}
                  placeholder="Name shown across the app"
                  helperText="How your name appears to others"
                />

                <TextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  disabled
                  size={isMobile ? 'small' : 'medium'}
                  helperText="Email cannot be changed"
                />

                <AppSelect
                  label="Timezone"
                  size={isMobile ? 'small' : 'medium'}
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <AppMenuItem value="America/New_York">Eastern Time (ET)</AppMenuItem>
                  <AppMenuItem value="America/Chicago">Central Time (CT)</AppMenuItem>
                  <AppMenuItem value="America/Denver">Mountain Time (MT)</AppMenuItem>
                  <AppMenuItem value="America/Los_Angeles">Pacific Time (PT)</AppMenuItem>
                  <AppMenuItem value="Asia/Kolkata">India Standard Time (IST)</AppMenuItem>
                  <AppMenuItem value="Europe/London">Greenwich Mean Time (GMT)</AppMenuItem>
                  <AppMenuItem value="Europe/Paris">Central European Time (CET)</AppMenuItem>
                </AppSelect>

                <TextField
                  label="Work Hours Per Day"
                  type="number"
                  value={workHoursPerDay}
                  onChange={(e) => setWorkHoursPerDay(Number(e.target.value))}
                  inputProps={{ min: 1, max: 24 }}
                  size={isMobile ? 'small' : 'medium'}
                  helperText="Used for bandwidth and capacity calculations"
                />

                <Button
                  variant="contained"
                  startIcon={<Save size={16} />}
                  onClick={handleSaveProfile}
                  disabled={profileMutation.isPending || !canEditOwnSettings}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, minHeight: isMobile ? 44 : 36 }}
                  fullWidth={isMobile}
                >
                  {profileMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </TabPanel>

          {/* Notifications Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ maxWidth: isMobile ? '100%' : 600 }}>
              <Typography variant="subtitle2" gutterBottom>
                Email Notifications
              </Typography>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={emailNotifications}
                        onChange={(e) => setEmailNotifications(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Email Notifications
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Receive email notifications for important updates
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', justifyContent: 'space-between', m: 0 }}
                    labelPlacement="start"
                  />
                </CardContent>
              </Card>

              <Typography variant="subtitle2" gutterBottom>
                Task Notifications
              </Typography>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={taskReminders}
                        onChange={(e) => setTaskReminders(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Task Reminders
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Get reminded about upcoming task deadlines
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', justifyContent: 'space-between', m: 0 }}
                    labelPlacement="start"
                  />
                  <Divider />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deadlineAlerts}
                        onChange={(e) => setDeadlineAlerts(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Deadline Alerts
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Alert when tasks are approaching or past deadline
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', justifyContent: 'space-between', m: 0 }}
                    labelPlacement="start"
                  />
                </CardContent>
              </Card>

              <Typography variant="subtitle2" gutterBottom>
                Review Notifications
              </Typography>
              <Card variant="outlined">
                <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reviewNotifications}
                        onChange={(e) => setReviewNotifications(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Review Notifications
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Get notified about performance review updates
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', justifyContent: 'space-between', m: 0 }}
                    labelPlacement="start"
                  />
                </CardContent>
              </Card>

              <Button
                variant="contained"
                startIcon={<Save size={16} />}
                onClick={handleSaveNotifications}
                disabled={notifMutation.isPending || !canEditOwnSettings}
                sx={{ mt: 3, minHeight: isMobile ? 44 : 36 }}
                fullWidth={isMobile}
              >
                {notifMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </TabPanel>

          {/* Timer & Work Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ maxWidth: isMobile ? '100%' : 600 }}>
              <Typography variant="subtitle2" gutterBottom>
                Timer Settings
              </Typography>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showTimerInHeader}
                        onChange={(e) => setShowTimerInHeader(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Show Timer in Header
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Display active timer in the top navigation bar
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', justifyContent: 'space-between', m: 0 }}
                    labelPlacement="start"
                  />
                  <Divider />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoStartTimer}
                        onChange={(e) => setAutoStartTimer(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Auto-Start Timer
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Automatically start timer when opening a task
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', justifyContent: 'space-between', m: 0 }}
                    labelPlacement="start"
                  />
                </CardContent>
              </Card>

              <Typography variant="subtitle2" gutterBottom>
                Pomodoro Mode
              </Typography>
              <Card variant="outlined">
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pomodoroMode}
                        onChange={(e) => setPomodoroMode(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Enable Pomodoro Mode
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Work in focused intervals with breaks
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', justifyContent: 'space-between', m: 0 }}
                    labelPlacement="start"
                  />

                  {pomodoroMode && (
                    <>
                      <Divider />
                      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                          label="Focus Duration (min)"
                          type="number"
                          value={pomodoroLength}
                          onChange={(e) => setPomodoroLength(Number(e.target.value))}
                          inputProps={{ min: 5, max: 60 }}
                          fullWidth
                          size={isMobile ? 'small' : 'medium'}
                        />
                        <TextField
                          label="Break Duration (min)"
                          type="number"
                          value={breakLength}
                          onChange={(e) => setBreakLength(Number(e.target.value))}
                          inputProps={{ min: 1, max: 30 }}
                          fullWidth
                          size={isMobile ? 'small' : 'medium'}
                        />
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>

              <Button
                variant="contained"
                startIcon={<Save size={16} />}
                onClick={handleSaveTimer}
                disabled={!canEditOwnSettings}
                sx={{ mt: 3, minHeight: isMobile ? 44 : 36 }}
                fullWidth={isMobile}
              >
                Save Changes
              </Button>
            </Box>
          </TabPanel>

          {/* Display Tab */}
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ maxWidth: isMobile ? '100%' : 600 }}>
              <Typography variant="subtitle2" gutterBottom>
                Theme
              </Typography>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={darkMode}
                        onChange={(e) => setDarkMode(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Dark Mode
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Use dark color scheme (Coming soon)
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', justifyContent: 'space-between', m: 0 }}
                    labelPlacement="start"
                    disabled
                  />
                </CardContent>
              </Card>

              <Typography variant="subtitle2" gutterBottom>
                Layout
              </Typography>
              <Card variant="outlined">
                <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={compactMode}
                        onChange={(e) => setCompactMode(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Compact Mode
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Reduce spacing and padding for denser display
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', justifyContent: 'space-between', m: 0 }}
                    labelPlacement="start"
                  />
                </CardContent>
              </Card>

              <Button
                variant="contained"
                startIcon={<Save size={16} />}
                onClick={handleSaveDisplay}
                disabled={!canEditOwnSettings}
                sx={{ mt: 3, minHeight: isMobile ? 44 : 36 }}
                fullWidth={isMobile}
              >
                Save Changes
              </Button>
            </Box>
          </TabPanel>

          {/* Task Categories Tab - Managers and above */}
          {canManageCategories && (
            <TabPanel value={activeTab} index={categoriesTabIndex}>
              <TaskCategoriesTab />
            </TabPanel>
          )}

          {/* Config Tab - Admin Only */}
          {canEditAdminConfig && (
            <TabPanel value={activeTab} index={configTabIndex}>
              <ConfigTab />
            </TabPanel>
          )}
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
